export type Loadable<T, TError = { message: string }> =
  | {
      status: "loading";
      data?: T | undefined;
      error?: undefined;
    }
  | {
      status: "success";
      data: T;
      error?: undefined;
    }
  | {
      status: "error";
      error: TError;
      data?: undefined;
    };

export type LoadableData<TLoadable extends Loadable<unknown, unknown>> =
  Extract<TLoadable, { status: "success" }>["data"];

export const transformLoadable = <T, U, TError = { message: string }>(
  loadable: Loadable<T, TError>,
  transformer: (data: T) => U,
): Loadable<U, TError> => {
  try {
    switch (loadable.status) {
      case "loading":
        return {
          status: "loading",
          data: loadable.data ? transformer(loadable.data) : undefined,
        };
      case "success":
        return { status: "success", data: transformer(loadable.data) };
      case "error":
        return { status: "error", error: loadable.error };
    }
  } catch (error: unknown) {
    return {
      status: "error",
      error: error as TError,
    };
  }
};

export const transformLoadables = <
  T extends unknown[],
  R,
  TError = { message: string },
>(
  loadables: [...{ [K in keyof T]: Loadable<T[K], TError> | undefined }],
  transformer: (...args: T) => R,
): Loadable<R, TError> => {
  if (loadables.find((l) => !l)) {
    return {
      status: "loading",
      data: undefined,
    };
  }
  const errorLoadable = loadables.find((l) => !l || l.status === "error");
  if (errorLoadable && errorLoadable.status === "error") {
    return {
      status: "error",
      error: errorLoadable.error,
    };
  }
  const loadingLoadable = loadables.find((l) => !l || l.status === "loading");
  if (loadingLoadable) {
    return {
      status: "loading",
      data: undefined,
    };
  }

  const partialData = loadables.map((l) => {
    if (!l) {
      return undefined;
    }
    if (l.status === "success") {
      return l.data;
    }
    if (l.status === "loading" && l.data !== undefined) {
      return l.data;
    }
    return undefined;
  }) as T;

  try {
    const transformed = transformer(...partialData);
    try {
      return {
        status: "success",
        data: transformed,
      };
    } catch (e) {
      return {
        status: "error",
        error: e as TError,
      };
    }
  } catch (e) {
    return {
      status: "error",
      error: e as TError,
    };
  }
};

export const mapLoadable = <T, U, TError>(
  loadables: Loadable<T, TError>[],
  transformer: (data: T) => U,
): Loadable<U, TError>[] => {
  return loadables.map((loadable) => transformLoadable(loadable, transformer));
};
